'use client';

import { useCallback, useEffect, useMemo } from 'react';
import React from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Breadcrumbs } from '@nx-frontend/shared/ui/breadcrumbs';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { useNavigateWithCheckUnsavedChanges } from '@nx-frontend/shared/hooks';
import { useRouter, usePathname, useParams, useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { $note } from '@nx-frontend/shared/store/note';
import { setNoteFullScreenEvent } from '@nx-frontend/shared/store/note-fullscreen';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $breadcrumbs, createBreadcrumbsEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { BreadcrumbsItem, MenuItem, MenuType } from '@nx-frontend/shared/types';
import { useAppNavHistory } from '@nx-frontend/shared/utils/use-app-nav-history';
import { BreadcrumbsBackButton } from './breadcrumbs-back-button';

interface NoteBreadCrumbsProps {
  noteMenuItem?: MenuItem | null;
  showButtomBack?: boolean;
  isCreateBreadcrumbs?: boolean;
  showFullPathForAllPages?: boolean;
}

export function NoteBreadcrumbs(props: NoteBreadCrumbsProps) {
  const { showButtomBack = true, isCreateBreadcrumbs = true, showFullPathForAllPages = true } = props;
  const router = useRouter();
  const params = useParams();
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const slug = useMemo(() => (params?.slug as string[]) || [], [params?.slug]);
  const [, , itemId] = slug;
  const [breadcrumbs, note, portalSettings, menuState] = useUnit([$breadcrumbs, $note, $portalSettings, $menuState]);
  const [createBreadcrumbs] = useUnit([createBreadcrumbsEvent]);
  const t = useTranslations();
  const portalPageUrl = searchParams?.get('portal-page-url') as string | undefined;

  const [setNoteFullScreen] = useUnit([setNoteFullScreenEvent]);

  const localizedBreadcrumbsPath = useMemo(() => {
    return breadcrumbs.path.map((breadcrumbsItem) => ({
      ...breadcrumbsItem,
      name: breadcrumbsItem.nameLocKey ? t(breadcrumbsItem.nameLocKey) : breadcrumbsItem.name,
    }));
  }, [breadcrumbs, t]);

  const createBreadcrumbsDebounce = useDebounce(createBreadcrumbs, 500);
  const navigateParams = useMemo(() => {
    return {
      routerPush: (url: string) => router.push(url),
    };
  }, [router]);
  const navigateWithCheckUnsavedChanges = useNavigateWithCheckUnsavedChanges(navigateParams);

  useEffect(() => {
    if (isCreateBreadcrumbs) {
      createBreadcrumbs(itemId);
    }
  }, [itemId, isCreateBreadcrumbs, createBreadcrumbs]);

  useEffect(() => {
    createBreadcrumbsDebounce.cancel();
    if (isCreateBreadcrumbs) {
      // rebuild breadcrumbs after menu was changed
      createBreadcrumbsDebounce(itemId);
    }
    return () => {
      // cancel debounce on component unmount as well
      createBreadcrumbsDebounce.cancel();
    };
  }, [itemId, menuState, createBreadcrumbsDebounce, isCreateBreadcrumbs]);

  const { wasPortalSharedNode, isPortalShared } = useMemo(() => {
    let wasPortalSharedNode = true;
    let isPortalShared = false;

    const isPortalPage = !!portalPageUrl;
    wasPortalSharedNode = note.isSharedForPortal || isPortalPage;
    let isShared = note.isSharedForPortal || isPortalPage || false;
    if (isShared) {
      if (-1 !== pathname.indexOf('/folder/')) {
        isShared = false;
        wasPortalSharedNode = false;
        isPortalShared = isShared;
        return { wasPortalSharedNode, isPortalShared };
      }
    }
    if (!isShared) {
      for (const menu of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR]) {
        if (menuState[menu].activeItemIds.length) {
          const id = menuState[menu].activeItemIds[0];
          if (id) {
            const item = menuState[menu]['entities'][id];
            if (item && item.url && note.globalId === item.targetId) {
              isShared = true;
              break;
            }
          }
        }
        if (isShared) {
          break;
        }
      }
    }
    if (!isShared) {
      let activePortalMenuItems = 0;
      for (const menu of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR]) {
        if (menuState[menu].activeItemIds.length) {
          const id = menuState[menu].activeItemIds[0];
          if (id) {
            activePortalMenuItems++;
          }
        }
      }
      if (0 === activePortalMenuItems) {
        wasPortalSharedNode = false;
      }
    }
    isPortalShared = isShared;

    return {
      wasPortalSharedNode,
      isPortalShared,
    };
  }, [note, menuState, pathname, portalPageUrl]);

  const history = useAppNavHistory(pathname);

  const handleBackClick = useCallback(() => {
    let prevPath = breadcrumbs.prevPath;

    if (!prevPath) {
      const prevUrl = history[history.length - 2];
      if (prevUrl) {
        prevPath = prevUrl;
      } else {
        prevPath =
          note.parentId === 'trash'
            ? `/space/${note.workspaceId}/all`
            : `/space/${note.workspaceId}/folder/${note.parentId}`;
      }
    }
    navigateWithCheckUnsavedChanges(prevPath);
    setNoteFullScreen(false);
  }, [
    breadcrumbs.prevPath,
    note.parentId,
    note.workspaceId,
    history,
    setNoteFullScreen,
    navigateWithCheckUnsavedChanges,
  ]);

  const handleOpenVisualClick = useCallback(async () => {
    if (!props.noteMenuItem && !portalPageUrl) {
      return;
    }
    let url = '/';

    if (portalPageUrl) {
      url = portalPageUrl;
    } else {
      for (const menu of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR]) {
        for (const k in menuState[menu].entities) {
          const item = menuState[menu].entities[k];
          if (item.id === props.noteMenuItem?.id) {
            url = item.url;
            break;
          }
        }
        if ('/' !== url) {
          break;
        }
      }
    }
    if (url) {
      navigateWithCheckUnsavedChanges(`/portal/${portalSettings.id}/visual${url}?open-publish-modal=true`);
    }
  }, [props, portalPageUrl, menuState, portalSettings.id, navigateWithCheckUnsavedChanges]);

  const handleCrumbClick = useCallback(
    (e: React.MouseEvent, item: BreadcrumbsItem) => {
      if (item.action) {
        item.action();
        return;
      }
      const isOpenInNewTab: boolean = e.metaKey || e.ctrlKey || !!item.openInNewTab;
      if (isOpenInNewTab) {
        window.open(item.url, '_blank');
        return;
      }
      navigateWithCheckUnsavedChanges(item.url);
      setNoteFullScreen(false);
    },
    [setNoteFullScreen, navigateWithCheckUnsavedChanges]
  );

  return (
    <div data-cy="breadcrumbs-trail" className="flex items-center gap-[24px]">
      {wasPortalSharedNode || isPortalShared ? (
        props.noteMenuItem || portalPageUrl ? (
          <button
            data-cy="breadcrumbs-back-button"
            onClick={handleOpenVisualClick}
            className="flex gap-2 items-center pt-[6px] pr-[16px] pl-[10px] justify-center rounded-[30px] hover:bg-zinc-50"
          >
            <Icon icon={IconsMap.ARROW_REPLY_1} className="w-[20px] h-[20px] text-cyan-900" />
            <span data-cy="open-in-portal-editor" className="text-[13px] font-bold text-cyan-900">
              Open in portal editor
            </span>
          </button>
        ) : null
      ) : (
        <>
          {showButtomBack ? <BreadcrumbsBackButton handleBackClick={handleBackClick} /> : null}
          <Breadcrumbs
            items={!breadcrumbs.allPagesVisited || showFullPathForAllPages ? localizedBreadcrumbsPath : []}
            onClick={handleCrumbClick}
          />
        </>
      )}
    </div>
  );
}
